import { flushSync } from "react-dom";

/**
 * Collapses a row without letting the scrollport clamp to a stale offset.
 * The clicked header stays where it was when it was on screen. When the
 * header sat above the scrollport (the user was at the bottom of the expanded
 * body), the header is placed at the bottom of the scrollport.
 */
export function preserveRowOnCollapse(anchor: HTMLElement, applyCollapse: () => void): void {
  const scroller = findScrollParent(anchor);
  const lockTarget = scroller ?? document.documentElement;
  const previousOverflowAnchor = lockTarget.style.overflowAnchor;
  lockTarget.style.overflowAnchor = "none";

  const before = readScrollport(scroller);
  const beforeRect = anchor.getBoundingClientRect();
  const headerWasAbove = beforeRect.bottom <= before.top + 1;

  flushSync(applyCollapse);

  const after = readScrollport(scroller);
  const rect = anchor.getBoundingClientRect();
  if (Math.abs(rect.top - beforeRect.top) <= 1) {
    requestAnimationFrame(() => {
      lockTarget.style.overflowAnchor = previousOverflowAnchor;
    });
    return;
  }

  const headerVisible = rect.bottom > after.top + 1 && rect.top < after.bottom - 1;
  if (headerWasAbove || !headerVisible) {
    const desiredTop = Math.max(after.top, after.bottom - rect.height);
    after.setScrollTop(after.scrollTop + (rect.top - desiredTop));
  } else {
    after.setScrollTop(after.scrollTop + (rect.top - beforeRect.top));
  }

  requestAnimationFrame(() => {
    lockTarget.style.overflowAnchor = previousOverflowAnchor;
  });
}

function findScrollParent(node: HTMLElement): HTMLElement | null {
  let current = node.parentElement;
  while (current) {
    const overflowY = getComputedStyle(current).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      current.scrollHeight > current.clientHeight + 1
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function readScrollport(scroller: HTMLElement | null): {
  top: number;
  bottom: number;
  scrollTop: number;
  setScrollTop: (value: number) => void;
} {
  if (scroller) {
    const rect = scroller.getBoundingClientRect();
    return {
      top: rect.top,
      bottom: rect.bottom,
      scrollTop: scroller.scrollTop,
      setScrollTop: (value) => {
        scroller.scrollTop = value;
      },
    };
  }
  const scrollingElement = document.scrollingElement ?? document.documentElement;
  return {
    top: 0,
    bottom: window.innerHeight,
    scrollTop: scrollingElement.scrollTop,
    setScrollTop: (value) => {
      scrollingElement.scrollTop = value;
    },
  };
}
